import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PERSONAS, PERSONA_HINTS, personaLabel } from '../../utils/permissions';
import { csrfFetch } from '../../utils/csrfFetch';

/**
 * PersonaAssignmentModal — assign org-wide default persona + per-location overrides (DOS-213).
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   orgId: number
 *   userId: number
 *   userLabel: string — display name or email
 *   locations: Array<{id, name}>  — all org locations this user is assigned to
 *   onSaved: () => void — called after any successful save
 *
 * Fetches:
 *   GET /api/personas/orgs/:orgId/members/:userId → { defaultPersona, locationOverrides[] }
 *
 * Saves:
 *   PUT /api/personas/orgs/:orgId/members/:userId { persona }
 *   POST /api/personas/orgs/:orgId/locations/:locId/members/:userId { persona }
 *   DELETE /api/personas/orgs/:orgId/locations/:locId/members/:userId
 */
export default function PersonaAssignmentModal({ isOpen, onClose, orgId, userId, userLabel, locations = [], onSaved }) {
  const [defaultPersona, setDefaultPersona] = useState('');
  const [overrides, setOverrides] = useState({}); // { [locationId]: persona }
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const closeBtnRef = useRef(null);

  const loadStack = useCallback(async () => {
    if (!orgId || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const r = await csrfFetch(`/api/personas/orgs/${orgId}/members/${userId}`, { credentials: 'include' });
      if (!r.ok) throw new Error('Failed to load persona stack');
      const data = await r.json();
      setDefaultPersona(data.defaultPersona || '');
      const map = {};
      for (const o of data.locationOverrides || []) {
        map[o.location_id] = o.persona;
      }
      setOverrides(map);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId, userId]);

  useEffect(() => {
    if (isOpen) loadStack();
  }, [isOpen, loadStack]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    setTimeout(() => closeBtnRef.current?.focus(), 50);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleSaveDefault = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await csrfFetch(`/api/personas/orgs/${orgId}/members/${userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ persona: defaultPersona || null }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.message || 'Failed to save default persona');
      }
      onSaved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleOverride = async (locationId, persona) => {
    setSaving(true);
    setError(null);
    try {
      if (!persona) {
        // Delete override
        const r = await csrfFetch(`/api/personas/orgs/${orgId}/locations/${locationId}/members/${userId}`, {
          method: 'DELETE', credentials: 'include',
        });
        if (!r.ok) throw new Error('Failed to remove override');
        setOverrides(prev => { const next = { ...prev }; delete next[locationId]; return next; });
      } else {
        const r = await csrfFetch(`/api/personas/orgs/${orgId}/locations/${locationId}/members/${userId}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ persona }),
        });
        if (!r.ok) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.message || 'Failed to save override');
        }
        setOverrides(prev => ({ ...prev, [locationId]: persona }));
      }
      onSaved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Assign Persona
          </h2>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4">
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Set the persona for <strong className="text-gray-900 dark:text-white">{userLabel}</strong>.
            Persona controls which skills and data this user sees. Default applies everywhere;
            per-location overrides shadow the default for that specific location.
          </p>

          {error && (
            <div className="mb-4 px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-8 text-center text-sm text-gray-500">Loading persona stack…</div>
          ) : (
            <>
              {/* Default persona */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Default persona (org-wide)
                </label>
                <div className="flex items-center gap-2">
                  <select
                    value={defaultPersona}
                    onChange={(e) => setDefaultPersona(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                      bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    disabled={saving}
                  >
                    <option value="">— No persona —</option>
                    {PERSONAS.map(p => (
                      <option key={p.name} value={p.name}>{p.label}</option>
                    ))}
                  </select>
                  <button
                    onClick={handleSaveDefault}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium rounded-md
                      bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50
                      disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
                {defaultPersona && PERSONA_HINTS[defaultPersona] && (
                  <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                    {PERSONA_HINTS[defaultPersona]}
                  </p>
                )}
              </div>

              {/* Per-location overrides */}
              {locations.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Per-location overrides ({locations.length} location{locations.length === 1 ? '' : 's'})
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                    Overrides only apply when the user's active location matches.
                    Leave blank to use the default persona above.
                  </p>
                  <div className="space-y-2">
                    {locations.map(loc => (
                      <div key={loc.id} className="flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {loc.name}
                          </div>
                          {overrides[loc.id] && (
                            <div className="text-xs text-purple-600 dark:text-purple-400">
                              Override: {personaLabel(overrides[loc.id])}
                            </div>
                          )}
                        </div>
                        <select
                          value={overrides[loc.id] || ''}
                          onChange={(e) => handleOverride(loc.id, e.target.value)}
                          disabled={saving}
                          className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md
                            bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">— Use default —</option>
                          {PERSONAS.map(p => (
                            <option key={p.name} value={p.name}>{p.label}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-md
              text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
